import DraftIcon from '@/components/icons/draft';
import { useLocalization } from '@/hooks/useLocalization';
import { UserContext } from '@/contexts/userContext';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useShowTagSelectorDropdown } from '@/hooks/useShowTagSelectorDropdown';
import { useViewingTagSelection } from '@/hooks/useViewingTagSelection';
import AdvisorTableHeader from '@/modules/document/advisorTableHeader';
import {
  Document2Record,
  useDownloadDocumentMutation,
  useGetDocuments2Query,
  usePreviewDocumentMutation,
} from '@/store/documentsApi';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { GridColDef, GridInitialState } from '@mui/x-data-grid-premium';
import { useContext, useMemo } from 'react';
import {
  APP_DATE_FORMAT,
  formatAppDate,
  parseOptionalDate,
} from '@/utils/date';
import { usePersonalStorageRowAction } from './usePersonalStorageRowAction';
import { DocVaultGrid } from './DocVaultGrid';
import { VaultEmptyOverlay, VaultListState } from './VaultListState';
import { getQueriesToRetry, getVaultListStatus } from './vaultListStatus';
import DocumentVaultTagEmptyState from './DocumentVaultTagEmptyState';
import {
  getDocumentDownloadName,
  getDocumentFilenameForBlobHandling,
  hasActiveDocumentVaultTag,
  isPersonalStorageDocument2,
  normalizeDocument2Records,
  shouldShowDocumentVaultTagEmptyState,
} from './documentVaultGridUtils';

import { GRID_COLUMN_WIDTH_NARROW } from '@/constants/dimensions';

const getUploadDate = (row: Document2Record): Date | null =>
  parseOptionalDate(row.UploadDate);

const initialGridState: GridInitialState = {
  sorting: { sortModel: [{ field: 'UploadDate', sort: 'desc' }] },
};

interface OverviewGridProps {
  searchTerm?: string;
  wealthAccountId?: number;
  vaultFileCategoryId?: number;
  investorId?: number;
  paginated?: boolean;
}

const OverviewGrid = ({
  searchTerm,
  wealthAccountId,
  vaultFileCategoryId,
  investorId,
  paginated = false,
}: OverviewGridProps) => {
  const { currentRole } = useContext(UserContext);
  const isAdvisor = (currentRole || '').toLowerCase() === 'advisor';
  const isAuthLoaded = useAuthIsLoaded();
  const showTagSelectorDropdown = useShowTagSelectorDropdown();
  const viewingTagSelection = useViewingTagSelection();
  const { t } = useLocalization();
  const hasSelectedViewingTag = hasActiveDocumentVaultTag(
    showTagSelectorDropdown,
    viewingTagSelection,
    investorId,
  );

  const isQuerySkipped = !isAuthLoaded || (isAdvisor && !investorId);

  const {
    currentData: documents2Data,
    isLoading: isDocumentsLoading,
    isFetching: isDocumentsFetching,
    isError: isDocumentsError,
    error: documentsError,
    isUninitialized: isDocumentsUninitialized,
    refetch: refetchDocuments,
  } = useGetDocuments2Query(
    {
      DateInterval: -1,
      WealthAccountId: wealthAccountId ? String(wealthAccountId) : '',
      VaultFileCategoryId: vaultFileCategoryId,
      AdvisorInvestorId: investorId,
    },
    {
      skip: isQuerySkipped,
    },
  );

  const [previewDocument, { isLoading: isPreviewing }] =
    usePreviewDocumentMutation();
  const [downloadDocument, { isLoading: isDownloading }] =
    useDownloadDocumentMutation();
  const { runPersonalStorageRowAction, isPersonalStorageActionPending } =
    usePersonalStorageRowAction({ investorId, wealthAccountId });
  const isRowActionPending =
    isPreviewing || isDownloading || isPersonalStorageActionPending;
  const isLoading = Boolean(isDocumentsLoading);
  const isFetching = Boolean(isDocumentsFetching);
  const documentsQuery = {
    isLoading,
    isFetching,
    isError: Boolean(isDocumentsError),
    isUninitialized: Boolean(isDocumentsUninitialized),
    hasData: Boolean(documents2Data),
    skip: isQuerySkipped,
  };
  const vaultListStatus = getVaultListStatus([documentsQuery]);
  const handleRetry = () => {
    if (getQueriesToRetry([documentsQuery]).length > 0) {
      void refetchDocuments();
    }
  };

  const handleRowAction = async (
    action: 'preview' | 'download' | 'folderClick',
    data: Document2Record,
  ) => {
    // v2 documents don't have folders, so we only handle preview/download.
    // Trust the action from the child component, no need to re-evaluate.
    // Personal Storage rows carry a VaultFiles id in VaultFile2Id, so they
    // must go through the personal/account endpoints, not Documents2.
    if (isPersonalStorageDocument2(data)) {
      await runPersonalStorageRowAction(action, data);
      return;
    }

    if (action === 'preview') {
      await previewDocument({
        vaultId: data.VaultFile2Id,
        InvestorId: investorId,
        fileName: getDocumentFilenameForBlobHandling(data),
      }).unwrap();
      return;
    }

    if (action === 'download') {
      await downloadDocument({
        vaultId: data.VaultFile2Id,
        filename: getDocumentDownloadName(data),
        InvestorId: investorId,
      }).unwrap();
      return;
    }
  };

  const rows = useMemo(() => {
    const normalized = normalizeDocument2Records(documents2Data);
    const term = (searchTerm ?? '').trim().toLowerCase();
    return term
      ? normalized.filter((row) => row.Name.toLowerCase().includes(term))
      : normalized;
  }, [documents2Data, searchTerm]);
  const shouldShowTagEmptyState = shouldShowDocumentVaultTagEmptyState({
    hasSelectedTag: hasSelectedViewingTag,
    searchTerm,
    isLoading,
    isFetching,
    rowCount: rows.length,
    isQuerySkipped: isAdvisor && !investorId,
  });

  const columns: GridColDef<Document2Record>[] = [
    {
      field: 'Name',
      headerName: 'File',
      flex: 1,
      renderCell: ({ row }) => (
        <Stack
          flexDirection='row'
          alignItems='center'
          gap={1}
        >
          <DraftIcon /> {row.Name}
        </Stack>
      ),
    },
    {
      field: 'UploadDate',
      headerName: t('date'),
      type: 'date',
      width: GRID_COLUMN_WIDTH_NARROW,
      valueGetter: (_value, row) => getUploadDate(row),
      valueFormatter: (value: Date | null) =>
        formatAppDate(value, { format: APP_DATE_FORMAT, fallback: '' }),
    },
  ];

  const tagKey = viewingTagSelection
    ? `${viewingTagSelection.source}:${String(viewingTagSelection.tagId)}`
    : '';
  const paginationResetKey = `${String(investorId ?? '')}|${searchTerm ?? ''}|${tagKey}`;

  const grid = (
    <Box>
      {isFetching && searchTerm && (
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          justifyContent='center'
          sx={{ mb: 1 }}
        >
          <CircularProgress size={16} />
          <Typography variant='caption'>Loading Search...</Typography>
        </Stack>
      )}
      <Box>
        {shouldShowTagEmptyState ? (
          <DocumentVaultTagEmptyState />
        ) : (
          <DocVaultGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            rowHeight={52}
            loading={isFetching || isRowActionPending}
            initialState={initialGridState}
            {...(paginated
              ? {
                  paginated: true,
                  paginationResetKey,
                  slots: {
                    noRowsOverlay: VaultEmptyOverlay,
                    noResultsOverlay: VaultEmptyOverlay,
                  },
                }
              : {
                  localeText: {
                    noRowsLabel: t('documentVault.noDocumentsFound'),
                  },
                })}
            listViewDateLabel={t('date')}
            getListViewDate={getUploadDate}
            onRowAction={handleRowAction}
            investorId={investorId}
            bulkDownloadContextName='overview'
            showBulkActions={false}
            showRowActions={false}
            useDocuments2BulkDownload
            enableXsListView
          />
        )}
      </Box>
      {!paginated && !shouldShowTagEmptyState && (
        <Box
          sx={{
            mt: 2,
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Typography variant='subtitle1'>Total Documents</Typography>
          <Typography variant='subtitle1'>{rows.length}</Typography>
        </Box>
      )}
    </Box>
  );

  const renderContent = () => {
    if (paginated) {
      return (
        <VaultListState
          status={vaultListStatus}
          onRetry={handleRetry}
        >
          {grid}
        </VaultListState>
      );
    }
    if (isLoading || (isFetching && !documents2Data)) {
      return <Typography>{t('loading')}</Typography>;
    }
    if (documentsError) {
      console.error('Error loading documents2:', documentsError);
      return <Typography color='error'>Error loading documents.</Typography>;
    }
    return grid;
  };

  return (
    <Stack
      gap={2}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {currentRole === 'advisor' && <AdvisorTableHeader />}
      {renderContent()}
    </Stack>
  );
};

export default OverviewGrid;
